import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FormikHelpers, useFormik } from 'formik';
import { useHistory } from 'react-router';
import { SERIES_TYPES } from 'shared/constants/pouchDB';
import { ROUTE } from 'shared/constants/routes_old';
import * as seriesActions from 'shared/modules/series/actions';
import { SERIES_SCHEMA } from 'shared/constants/general';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
} from '@mui/material';
import { FormTextField } from 'shared/_ui/form/form-text-field';
import { DocType, TDocSeries } from 'shared/constants/docs';
import SwitchField from 'shared/_ui/form/switch-field';
import { FormTagField } from 'shared/_ui/form/form-tag-field';
import { FormSelectField } from 'shared/_ui/form/form-select-field';

type NewSeries = Omit<TDocSeries, '_id'>;

const NEW_SERIES: NewSeries = {
	type: DocType.SERIES,
	title: '',
	description: '',
	keywords: '',
	seriesType: 'video',
	seasons: false,
	sponsored: false,
};

export default function SeriesCreate({ onClose }: { onClose: () => void }) {
	const dispatch = useDispatch();
	const history = useHistory();

	const handleSubmit = useCallback(
		async (values: NewSeries, { setSubmitting }: FormikHelpers<NewSeries>) => {
			setSubmitting(true);
			try {
				const newSeries = await dispatch(seriesActions.insert(values));
				dispatch(seriesActions.readSeriesList());
				history.push(ROUTE.series.id((newSeries as any)._id));
			} finally {
				setSubmitting(false);
			}
		},
		[dispatch, history]
	);

	const formik = useFormik<NewSeries>({
		initialValues: NEW_SERIES,
		onSubmit: handleSubmit,
		validationSchema: SERIES_SCHEMA,
	});
	const { isSubmitting, isValid } = formik;

	return (
		<Dialog open fullWidth maxWidth="sm">
			<DialogTitle variant="h2">New Series</DialogTitle>
			<DialogContent>
				<form onSubmit={formik.handleSubmit}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 1.5,
						}}
					>
						<FormTextField
							name="title"
							formik={formik}
							fullWidth
							label="Name:"
							labelProps={{ position: 'top' }}
							required
							spellCheck
							autoFocus
						/>
						<FormTextField
							name="description"
							formik={formik}
							fullWidth
							multiline
							minRows={2}
							label="Description:"
							labelProps={{ position: 'top' }}
							required
							spellCheck
						/>
						<FormTagField
							label="Keywords:"
							name="keywords"
							fullWidth
							slotProps={{ htmlInput: { maxLength: 500 } }}
							formik={formik}
							labelProps={{ position: 'top' }}
							// showCopyButton // TODO: Implement copy tags functionality
						/>
						<FormSelectField
							name="seriesType"
							formik={formik}
							label="Type:"
							labelProps={{ position: 'top' }}
						>
							{Object.keys(SERIES_TYPES).map((key) => (
								<MenuItem key={key} value={key}>
									{key}
								</MenuItem>
							))}
						</FormSelectField>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								flexWrap: 'wrap',
							}}
						>
							<SwitchField name="sponsored" formik={formik} label="Sponsored:" />
							<SwitchField name="seasons" formik={formik} label="Seasons:" />
						</Box>
					</Box>
				</form>
			</DialogContent>
			<DialogActions>
				<Button color="secondary" onClick={onClose} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button
					type="submit"
					disabled={isSubmitting || !isValid}
					onClick={() => formik.handleSubmit()}
				>
					Create
				</Button>
			</DialogActions>
		</Dialog>
	);
}
